import { X } from 'lucide-react'
import * as React from 'react'
import { Badge as UIBadge } from '../ui/badge'
import { Button } from '../ui/button'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

type Color =
  | 'gray'
  | 'green'
  | 'red'
  | 'blue'
  | 'yellow'
  | 'purple'
  | 'indigo'
  | 'pink'
type Size = 'sm' | 'md' | 'lg'
type Variant = 'default' | 'secondary' | 'outline' | 'soft'

export interface BadgeProps extends Omit<
  React.ComponentProps<typeof UIBadge>,
  'variant'
> {
  color?: Color
  variant?: Variant
  size?: Size
  icon?: LucideIcon
  iconPosition?: 'left' | 'right'
  removable?: boolean
  onRemove?: () => void
}

const colorVariants: Record<Color, Record<Variant, string>> = {
  gray: {
    default: 'bg-gray-900 text-white border-gray-900',
    secondary: 'bg-gray-100 text-gray-900 border-gray-200',
    outline: 'bg-transparent text-gray-700 border-gray-300',
    soft: 'bg-gray-100/80 text-gray-700 border-gray-200/50',
  },
  green: {
    default: 'bg-emerald-600 text-white border-emerald-600',
    secondary: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    outline: 'bg-transparent text-emerald-600 border-emerald-300',
    soft: 'bg-emerald-50/80 text-emerald-700 border-emerald-200/50',
  },
  red: {
    default: 'bg-red-600 text-white border-red-600',
    secondary: 'bg-red-50 text-red-700 border-red-200',
    outline: 'bg-transparent text-red-600 border-red-300',
    soft: 'bg-red-50/80 text-red-700 border-red-200/50',
  },
  blue: {
    default: 'bg-blue-600 text-white border-blue-600',
    secondary: 'bg-blue-50 text-blue-700 border-blue-200',
    outline: 'bg-transparent text-blue-600 border-blue-300',
    soft: 'bg-blue-50/80 text-blue-700 border-blue-200/50',
  },
  yellow: {
    default: 'bg-amber-500 text-white border-amber-500',
    secondary: 'bg-amber-50 text-amber-700 border-amber-200',
    outline: 'bg-transparent text-amber-600 border-amber-300',
    soft: 'bg-amber-50/80 text-amber-700 border-amber-200/50',
  },
  purple: {
    default: 'bg-purple-600 text-white border-purple-600',
    secondary: 'bg-purple-50 text-purple-700 border-purple-200',
    outline: 'bg-transparent text-purple-600 border-purple-300',
    soft: 'bg-purple-50/80 text-purple-700 border-purple-200/50',
  },
  indigo: {
    default: 'bg-indigo-600 text-white border-indigo-600',
    secondary: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    outline: 'bg-transparent text-indigo-600 border-indigo-300',
    soft: 'bg-indigo-50/80 text-indigo-700 border-indigo-200/50',
  },
  pink: {
    default: 'bg-pink-600 text-white border-pink-600',
    secondary: 'bg-pink-50 text-pink-700 border-pink-200',
    outline: 'bg-transparent text-pink-600 border-pink-300',
    soft: 'bg-pink-50/80 text-pink-700 border-pink-200/50',
  },
}

const sizeVariants: Record<
  Size,
  {
    container: string
    text: string
    iconPx: number
    closeIconPx: number
  }
> = {
  sm: {
    container: 'px-2 py-0.5 text-xs rounded-md',
    text: 'text-xs',
    iconPx: 14,
    closeIconPx: 10,
  },
  md: {
    container: 'px-2.5 py-1 text-sm rounded-md',
    text: 'text-sm',
    iconPx: 16,
    closeIconPx: 12,
  },
  lg: {
    container: 'px-3 py-1.5 text-base rounded-lg',
    text: 'text-base',
    iconPx: 20,
    closeIconPx: 14,
  },
}

export function Badge({
  children,
  color = 'gray',
  variant = 'secondary',
  size = 'sm',
  icon: Icon,
  iconPosition = 'left',
  removable = false,
  onRemove,
  className,
  ...props
}: BadgeProps) {
  const colorClasses = colorVariants[color][variant]
  const sizeConfig = sizeVariants[size]

  return (
    <UIBadge
      {...props}
      className={cn(
        'inline-flex items-center gap-1.5 font-medium border transition-colors',
        colorClasses,
        sizeConfig.container,
        sizeConfig.text,
        className,
      )}
    >
      {Icon && iconPosition === 'left' && (
        <Icon size={sizeConfig.iconPx} className="shrink-0" />
      )}

      {children && <span className="truncate">{children}</span>}

      {Icon && iconPosition === 'right' && (
        <Icon size={sizeConfig.iconPx} className="shrink-0" />
      )}

      {removable && onRemove && (
        <Button
          variant="ghost"
          size="icon"
          onClick={(e) => {
            e.stopPropagation()
            onRemove()
          }}
          className={cn(
            'ml-1 h-auto w-auto rounded-full hover:bg-current/10 focus-visible:ring-current/20 hover:cursor-pointer',
            size === 'sm' && 'p-0.5',
            size === 'md' && 'p-0.5',
            size === 'lg' && 'p-1',
          )}
          aria-label="Remove"
        >
          <X size={sizeConfig.closeIconPx} className="shrink-0" />
        </Button>
      )}
    </UIBadge>
  )
}
